import { useCallback, useEffect, useState } from 'react';

import { AppBar, Box, Fab, Toolbar, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { format } from 'date-fns';
import { useModal } from 'react-modal-hook';

import AppliedFilters from 'components/AppliedFilters';
import FilterButton from 'components/FilterButton';
import ModalAddAgendamento from 'components/ModalAddAgendamento';
import SearchBar from 'components/SearchBar';
import useCurrentUser from 'hooks/useCurrentUser';
import useFilters from 'hooks/useFilters';
import { useAppDispatch, useAppSelector } from 'store';
import {
  agendamentosSelector,
  AgendamentoStatusTypes,
  fetchAgendamentosV2,
  resetAgendamentosV2,
  resetIdsAgendamentosSelecionados,
} from 'store/reducers/agendamentos';
import { FilterPropertyType, IFiltersOptions } from 'types';

import ListaAgendamentos from './components/ListaAgendamentos';
import ModalFilters, {
  defaultModalFilters,
  ModalFilterOptionsType,
} from './components/ModalFilters';

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  filtersContainer: {
    overflow: 'hidden',
    transition: 'height 0.3s ease',
  },
  filtersOpen: {
    height: 0,
  },
  filtersClosed: {
    height: 'auto',
  },
}));

export type PayloadType = {
  page?: number;
  limit?: number;
  status?: AgendamentoStatusTypes;
  attendantId: string;
  date?: string;
  teamId?: string;
  channelId?: string;
  contact?: string;
};

function Agendamentos() {
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const [search, setSearch] = useState('');
  const [appliedFilters, setAppliedFilters] =
    useState<ModalFilterOptionsType>(defaultModalFilters);

  const {
    filters: chips,
    setFilters,
    onRemoveFilter,
    onRemoveAllFilters,
  } = useFilters([]);

  const dispatch = useAppDispatch();
  const {
    agendamentosV2,
    agendamentosV2InfoPage: { total, page },
  } = useAppSelector(agendamentosSelector);

  const hasMore = total === null || agendamentosV2.length < total;

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleApplyFilters = useCallback(
    (filtersObj: ModalFilterOptionsType, newChips: IFiltersOptions[]) => {
      setAppliedFilters(filtersObj);
      setFilters(newChips);
    },
    [setFilters],
  );

  const handleRemoveFilter = useCallback(
    (property: string) => {
      onRemoveFilter(property as FilterPropertyType);
      setAppliedFilters(prev => {
        const updates: Partial<ModalFilterOptionsType> = {};
        if (property === 'status') {
          updates.status = '';
        } else if (property === 'date') {
          updates.date = null;
        } else if (property === 'teamId') {
          updates.teamId = '';
          updates.team = undefined;
        } else if (property === 'channelCode') {
          updates.channelCode = '';
          updates.canal = undefined;
        }
        return { ...prev, ...updates };
      });
    },
    [onRemoveFilter],
  );

  const handleResetFilters = useCallback(() => {
    onRemoveAllFilters();
    setAppliedFilters(defaultModalFilters);
  }, [onRemoveAllFilters]);

  const handleResetAllFiltersAndSearch = useCallback(() => {
    setSearch('');
    handleResetFilters();
  }, [handleResetFilters]);

  const hasFiltersApplied =
    chips.length > 0 || search !== '' || appliedFilters.status !== '';

  const [showModalAdd, hideModalAdd] = useModal(
    ({ in: open }) => (
      <ModalAddAgendamento
        open={open}
        onClose={hideModalAdd}
        onSuccess={handleResetAllFiltersAndSearch}
      />
    ),
    [handleResetAllFiltersAndSearch],
  );

  const getAgendamentos = useCallback(
    (pageNumber = 1) => {
      if (!currentUser) return;

      const payload: PayloadType = { attendantId: currentUser.id };

      if (appliedFilters.status) payload.status = appliedFilters.status;
      if (appliedFilters.date)
        payload.date = format(appliedFilters.date, 'yyyy-MM-dd');
      if (appliedFilters.teamId) payload.teamId = appliedFilters.teamId;
      if (appliedFilters.channelCode)
        payload.channelId = appliedFilters.channelCode;
      if (search) payload.contact = search;

      dispatch(
        fetchAgendamentosV2({
          page: pageNumber,
          ...payload,
        }),
      ).then(() => dispatch(resetIdsAgendamentosSelecionados()));
    },
    [currentUser, dispatch, appliedFilters, search],
  );

  const [showModalFilters, hideModalFilters] = useModal(
    ({ in: open }) => (
      <ModalFilters
        open={open}
        onClose={hideModalFilters}
        initialFilters={appliedFilters} // abre sempre com o estado atual aplicado
        onApply={handleApplyFilters}
        onClear={handleResetFilters}
      />
    ),
    [appliedFilters, handleApplyFilters, handleResetFilters],
  );

  const handleLoadNext = useCallback(() => {
    const nextPage = page + 1;
    getAgendamentos(nextPage);
  }, [getAgendamentos, page]);

  useEffect(() => {
    dispatch(resetAgendamentosV2());
    getAgendamentos();
  }, [dispatch, getAgendamentos]);

  return (
    <Box className={classes.root}>
      <AppBar position="relative" color="transparent" elevation={0}>
        <Toolbar style={{ minHeight: 48 }} disableGutters>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            mb={1}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="h6">Agendamentos</Typography>

              <Box flexGrow={1} ml={2} mr={2}>
                <Fab size="small" color="primary" onClick={showModalAdd}>
                  <AddIcon />
                </Fab>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gridGap={8}>
              <Box flex={1} minWidth={280}>
                <SearchBar
                  value={search}
                  onChange={handleSearch}
                  onCancelSearch={() => handleSearch('')}
                />
              </Box>

              <FilterButton onClick={showModalFilters} />
            </Box>
          </Box>
        </Toolbar>

        {chips.length > 0 && (
          <AppliedFilters
            filters={chips}
            onRemove={handleRemoveFilter}
            onRemoveAll={handleResetFilters}
          />
        )}
      </AppBar>

      <ListaAgendamentos
        onLoadNext={handleLoadNext}
        hasMore={hasMore}
        hasFiltersApplied={hasFiltersApplied}
        onSuccess={() => {
          dispatch(resetAgendamentosV2());
          getAgendamentos();
          handleResetAllFiltersAndSearch();
        }}
      />
    </Box>
  );
}

export default Agendamentos;
